import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarBlankIcon, PlusIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  StatusChip,
  fmtRelativeTime,
  type Tone,
} from '@smena/ui';
import { embedCard, embedContent, embedHeader, type EmbeddableCardProps } from './embed';
import { createIcalFeed, deleteIcalFeed, fetchIcalFeeds, type IcalFeed } from './api';

/**
 * «iCal-фиды»: список адресов, которые поллер обходит раз в 15 минут.
 *
 * До этой карточки на «Интеграциях» стоял один счётчик - человек видел «3 фида», но не знал
 * какие, живы ли они и как убрать лишний. Ручка `DELETE /api/staff/ingest/ical/feeds/:id`
 * при этом была готова.
 *
 * Отключённые фиды (`active: false`) в списке не показываем: сервер удаляет их мягко, но для
 * УК «удалил» значит «его больше нет». Историю по ним даёт лента событий ниже на странице.
 */
export function IcalFeedsCard({ embedded }: EmbeddableCardProps = {}) {
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');

  const q = useQuery({ queryKey: ['ical-feeds'], queryFn: fetchIcalFeeds, refetchInterval: 60_000 });

  const createMut = useMutation({
    mutationFn: createIcalFeed,
    onSuccess: () => {
      setUrl('');
      setLabel('');
      toast.success('Фид добавлен - первый обход в ближайшие 15 минут');
      void qc.invalidateQueries({ queryKey: ['ical-feeds'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось добавить фид'),
  });

  // Ту же проверку делает сервер (ingest.controller.ts). Дублируем на клиенте, чтобы человек
  // увидел ошибку до отправки: адрес iCal обычно вставляют из буфера и легко захватить лишнее.
  const urlValid = /^https?:\/\//i.test(url.trim());

  const deleteMut = useMutation({
    mutationFn: deleteIcalFeed,
    onSuccess: () => {
      setConfirmId(null);
      toast.success('Фид удалён');
      void qc.invalidateQueries({ queryKey: ['ical-feeds'] });
      void qc.invalidateQueries({ queryKey: ['integrations-health'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить фид'),
  });

  const feeds = (q.data ?? []).filter((f) => f.active);

  return (
    <Card className={embedCard(embedded)}>
      <CardHeader className={embedHeader(embedded, 'flex flex-row items-center justify-between gap-3')}>
        <CardTitle className="flex items-center gap-2">
          <CalendarBlankIcon weight="bold" className="size-4 text-accent-fg" /> iCal-фиды
        </CardTitle>
        {q.data ? (
          <StatusChip tone={feeds.length ? 'green' : 'muted'}>
            {feeds.length ? `${feeds.length} ${pluralFeeds(feeds.length)}` : 'нет фидов'}
          </StatusChip>
        ) : null}
      </CardHeader>

      <CardContent className={embedContent(embedded, 'flex flex-col gap-3 text-sm')}>
        <form
          className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!urlValid || createMut.isPending) return;
            createMut.mutate({ url: url.trim(), label: label.trim() || null });
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="ical-url">Адрес iCal</Label>
            <Input
              id="ical-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://sutochno.ru/ical/…"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ical-label">Название</Label>
            <Input
              id="ical-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Например, Суточно - студия на Мира"
              maxLength={120}
            />
          </div>
          <p className="text-[11px] text-muted">
            Ссылку на календарь площадка отдаёт в настройках объекта: ищите «Экспорт календаря»,
            iCal или ICS. Название нужно только вам - по нему проще искать фид в списке.
          </p>
          <div>
            <Button type="submit" size="sm" disabled={!urlValid || createMut.isPending}>
              <PlusIcon weight="bold" className="size-4" />
              {createMut.isPending ? 'Добавляем…' : 'Добавить фид'}
            </Button>
          </div>
          {url.trim() && !urlValid ? (
            <span className="text-[11px] text-red">Адрес должен начинаться с http:// или https://</span>
          ) : null}
        </form>

        {q.isLoading ? (
          <p className="text-muted">Загружаем…</p>
        ) : q.isError ? (
          <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-3 text-xs text-red">
            <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>
              Не удалось получить список:{' '}
              {q.error instanceof ApiError ? q.error.message : 'ошибка сервера'}.
            </span>
          </div>
        ) : feeds.length === 0 ? (
          <EmptyState
            icon={<CalendarBlankIcon weight="bold" />}
            title="Фидов нет"
            description="Брони из календарей площадок - Суточно, Островок, Airbnb - подтягиваются только по добавленному адресу iCal. Пока адресов нет, этот источник молчит."
          />
        ) : (
          <>
            <p className="text-muted">Проверяем новые брони раз в 15 минут.</p>
            <ul className="flex flex-col gap-2">
              {feeds.map((feed) => (
                <li
                  key={feed.id}
                  className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{feed.label ?? 'Без названия'}</div>
                      {/* Полный адрес - в title: он длинный и в нём часто зашит токен площадки. */}
                      <div className="mt-0.5 font-mono text-[11px] break-all text-muted" title={feed.url}>
                        {shortenUrl(feed.url)}
                      </div>
                      <div className="mt-1 text-[11px] text-muted">
                        {feed.lastPolledAt
                          ? `Синхронизация: ${fmtRelativeTime(feed.lastPolledAt)}`
                          : 'Ещё не синхронизировали'}
                        {feed.lastEventsCount !== null ? ` · событий: ${feed.lastEventsCount}` : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <StatusChip tone={statusTone(feed)}>{statusLabel(feed)}</StatusChip>
                      {confirmId !== feed.id ? (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmId(feed.id)}>
                          <TrashIcon weight="bold" className="size-4" /> Удалить
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {feed.lastError ? (
                    <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-2 text-[11px] text-red">
                      <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
                      <span className="break-words">{feed.lastError}</span>
                    </div>
                  ) : null}

                  {confirmId === feed.id ? (
                    <div className="flex flex-col gap-2 border-t border-line pt-2">
                      <span className="text-xs text-amber">
                        Календарь перестанет опрашиваться - новые брони и отмены с этой площадки
                        перестанут приходить. Уже загруженные брони останутся.
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={deleteMut.isPending}
                          onClick={() => deleteMut.mutate(feed.id)}
                        >
                          {deleteMut.isPending ? 'Удаляем…' : 'Всё равно удалить'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>
                          Отмена
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Адреса iCal длинные и однообразные, различаются хвостом - оставляем начало и конец. */
function shortenUrl(url: string, max = 56): string {
  const bare = url.replace(/^https?:\/\//i, '');
  if (bare.length <= max) return bare;
  const head = Math.ceil((max - 1) / 2);
  const tail = max - 1 - head;
  return `${bare.slice(0, head)}…${bare.slice(-tail)}`;
}

function statusTone(feed: IcalFeed): Tone {
  if (feed.lastStatus === 'ok') return 'green';
  if (feed.lastStatus === 'error') return 'red';
  if (feed.lastStatus === 'ok_with_errors') return 'amber';
  return 'muted';
}

function statusLabel(feed: IcalFeed): string {
  if (feed.lastStatus === 'ok') return 'работает';
  if (feed.lastStatus === 'error') return 'ошибка';
  if (feed.lastStatus === 'ok_with_errors') return 'с ошибками';
  return 'ждём первый обход';
}

function pluralFeeds(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'фид';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'фида';
  return 'фидов';
}
